/**
 * Componente de Lista Virtualizada reutilizável
 * Para painéis de análise com muitos itens
 * Suporta scroll interno (padrão) ou scroll da página (usePageScroll)
 */

import React, { useCallback, useRef, useMemo, memo, useEffect, useState } from 'react'
import { VariableSizeList as List, ListChildComponentProps } from 'react-window'
import { AutoSizer } from 'react-virtualized-auto-sizer'
import { Box, Typography, Paper, Button, useMediaQuery, useTheme } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'

// Constantes de altura
const DEFAULT_ITEM_HEIGHT = 120
const DEFAULT_ITEM_HEIGHT_MOBILE = 90

interface VirtualizedAnalysisListProps<T> {
  items: T[]
  renderItem: (item: T, index: number, style?: React.CSSProperties) => React.ReactNode
  getItemHeight?: (item: T, index: number) => number
  emptyMessage?: string
  emptySubMessage?: string
  onClearFilters?: () => void
  minHeight?: number
  /** Index do item expandido - quando muda, força recálculo das alturas */
  expandedItemIndex?: number | string | null
  /** Se true, usa o scroll da página em vez do scroll interno */
  usePageScroll?: boolean
}

function VirtualizedAnalysisListInner<T>({
  items,
  renderItem,
  getItemHeight,
  emptyMessage = 'Nenhum item encontrado',
  emptySubMessage = 'Tente ajustar os filtros',
  onClearFilters,
  minHeight = 400,
  expandedItemIndex,
  usePageScroll = false
}: VirtualizedAnalysisListProps<T>) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const listRef = useRef<List>(null)
  const listContainerRef = useRef<HTMLDivElement>(null)
  const itemHeightsRef = useRef<Record<number, number>>({})

  // Estado para scroll da página - usar window.innerHeight dinâmico
  const [containerWidth, setContainerWidth] = useState<number>(0)
  const [windowHeight, setWindowHeight] = useState<number>(typeof window !== 'undefined' ? window.innerHeight : 600)

  const defaultHeight = isMobile ? DEFAULT_ITEM_HEIGHT_MOBILE : DEFAULT_ITEM_HEIGHT

  // Calcular altura do item
  const getItemSize = useCallback((index: number): number => {
    if (itemHeightsRef.current[index]) {
      return itemHeightsRef.current[index]
    }

    if (getItemHeight && items[index]) {
      const height = getItemHeight(items[index], index)
      itemHeightsRef.current[index] = height
      return height
    }

    return defaultHeight
  }, [items, getItemHeight, defaultHeight])

  // Calcular altura total para page scroll
  const totalListHeight = useMemo(() => {
    if (!usePageScroll) return 0
    let total = 0
    for (let i = 0; i < items.length; i++) {
      total += getItemSize(i)
    }
    return total
  }, [items, getItemSize, usePageScroll])

  // Componente de row
  const Row = useCallback(({ index, style }: ListChildComponentProps) => {
    const item = items[index]

    return (
      <>
        {renderItem(item, index, style)}
      </>
    )
  }, [items, renderItem])

  // Reset cache quando items mudam
  useMemo(() => {
    itemHeightsRef.current = {}
    listRef.current?.resetAfterIndex(0)
  }, [items])

  // Reset cache quando item é expandido/colapsado
  useEffect(() => {
    itemHeightsRef.current = {}
    listRef.current?.resetAfterIndex(0)
  }, [expandedItemIndex])

  // Track window dimensions and container width (page scroll mode)
  useEffect(() => {
    if (!usePageScroll) return

    const updateDimensions = () => {
      setWindowHeight(window.innerHeight)
      if (listContainerRef.current) {
        setContainerWidth(listContainerRef.current.offsetWidth)
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [usePageScroll])

  // Scroll handler para page scroll mode
  useEffect(() => {
    if (!usePageScroll || !listRef.current || !listContainerRef.current) return

    const handleScroll = () => {
      if (!listContainerRef.current || !listRef.current) return

      const containerRect = listContainerRef.current.getBoundingClientRect()
      // Calcular quanto do container está acima da viewport
      const scrollOffset = Math.max(0, -containerRect.top)

      listRef.current.scrollTo(scrollOffset)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial call

    return () => window.removeEventListener('scroll', handleScroll)
  }, [usePageScroll, items])

  if (items.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', bgcolor: '#f5f5f5', '#monkee-file-transcript-client &': { backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' } }}>
        <FilterListIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: '#bdbdbd', mb: 1 }} />
        <Typography variant="body1" sx={{ color: '#666', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {emptyMessage}
        </Typography>
        <Typography variant="body2" sx={{ color: '#999', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {emptySubMessage}
        </Typography>
        {onClearFilters && (
          <Button variant="outlined" size="small" onClick={onClearFilters}>
            Limpar filtros
          </Button>
        )}
      </Paper>
    )
  }

  // Page scroll mode - usa viewport sticky igual ao TranscriptViewerVirtualized
  if (usePageScroll) {
    return (
      <Box
        ref={listContainerRef}
        sx={{
          height: totalListHeight,
          position: 'relative',
          width: '100%',
          bgcolor: '#fff',
          '#monkee-file-transcript-client &': { backgroundColor: '#fff !important', background: '#fff !important' }
        }}
      >
        {/* Viewport sticky que contém a lista virtualizada */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            height: windowHeight,
            overflow: 'hidden',
            width: '100%',
            bgcolor: '#fff',
            '#monkee-file-transcript-client &': { backgroundColor: '#fff !important', background: '#fff !important' }
          }}
        >
          <List
            ref={listRef}
            height={windowHeight}
            width={containerWidth || '100%'}
            itemCount={items.length}
            itemSize={getItemSize}
            estimatedItemSize={defaultHeight}
            overscanCount={5}
            style={{ overflow: 'hidden' }}
          >
            {Row}
          </List>
        </Box>
      </Box>
    )
  }

  // Internal scroll mode (padrão)
  return (
    <Box sx={{ height: minHeight, minHeight }}>
      <AutoSizer
        renderProp={({ height, width }) => (
          <List
            ref={listRef}
            height={height ?? minHeight}
            width={width ?? 600}
            itemCount={items.length}
            itemSize={getItemSize}
            estimatedItemSize={defaultHeight}
            overscanCount={3}
          >
            {Row}
          </List>
        )}
      />
    </Box>
  )
}

// Exportar memoizado
const VirtualizedAnalysisList = memo(VirtualizedAnalysisListInner) as typeof VirtualizedAnalysisListInner

export default VirtualizedAnalysisList
